import { Alert, AlertDescription, AlertIcon, Box, Button, Flex, Icon, Text } from "@chakra-ui/react";
import { Tooltip } from "../../../common/components/Tooltip";
import { useTranslation } from "react-i18next";
import { FiAlertTriangle } from "react-icons/fi";
import {
  BidStatus,
  type EvalBidDto,
  type SourcingEventDto,
  useGetEvaluationQuery,
} from "../../../autogen/bff-api";
import { useFinalStageRound } from "../rounds/RoundSelectionContext";
import { formatMoney } from "../../../common/formatting/displaySum";

export const useStaleScoreMessage = (): string => {
  const { t } = useTranslation();
  return String(
    t("This score was given before the supplier last delivered, so it may not describe the current tender"),
  );
};

const useStalePriceOverrideMessage = (): string => {
  const { t } = useTranslation();
  return String(
    t("This price was set before the supplier last delivered, so it may not describe the current tender"),
  );
};

const StaleIcon = ({ message }: { message: string }) => (
  <Tooltip label={message} openDelay={200}>
    <Box as="span" display="inline-flex" color="smWarning" role="img" aria-label={message}>
      <Icon as={FiAlertTriangle} boxSize="3.5" />
    </Box>
  </Tooltip>
);

export const StaleScoreIcon = () => <StaleIcon message={useStaleScoreMessage()} />;

export const StalePriceOverrideIcon = () => <StaleIcon message={useStalePriceOverrideMessage()} />;

export const StaleScoreNotice = ({ onKeep }: { onKeep?: () => void }) => {
  const { t } = useTranslation();
  const message = useStaleScoreMessage();
  return (
    <Flex align="center" gap="2" color="smWarning" fontSize="sm" wrap="wrap">
      <Icon as={FiAlertTriangle} boxSize="3.5" flexShrink={0} />
      <Text>{message}</Text>
      {onKeep && (
        <Button size="xs" variant="outline" onClick={onKeep}>
          {t("Keep this score")}
        </Button>
      )}
    </Flex>
  );
};

export const hasDecisionOlderThanDelivery = (bid: EvalBidDto): boolean =>
  bid.cells.some((cell) => cell.scoredBeforeLatestDelivery) || bid.priceOverride?.setBeforeLatestDelivery === true;

export const StaleDecisionsBanner = ({
  bids,
  evaluationCompleted = false,
}: {
  bids: EvalBidDto[];
  evaluationCompleted?: boolean;
}) => {
  const { t } = useTranslation();
  const count = bids.filter((bid) => bid.status !== BidStatus.Rejected && hasDecisionOlderThanDelivery(bid)).length;
  if (count === 0) return null;
  return (
    <Alert status="warning" rounded="lg">
      <AlertIcon />
      <AlertDescription fontSize="sm">
        {t(
          "{{count}} bids were delivered again after you assessed them. Scores and prices marked with a warning sign describe an earlier version of the bid",
          { count },
        )}
        {" "}
        {evaluationCompleted
          ? t("Reopen the evaluation to check the new version, then keep or change each marked score and price")
          : t("Open the bid, check the new version, then keep or change each marked score and price")}
      </AlertDescription>
    </Alert>
  );
};

const roundTenderAwaitsAssessment = (bid: EvalBidDto): boolean =>
  bid.status !== BidStatus.Rejected &&
  bid.earlierAssessment != null &&
  bid.priceOverride == null &&
  !bid.cells.some((cell) => !cell.isAutoComputed && cell.points != null);

export const EarlierAssessmentBanner = ({
  bids,
  evaluationCompleted = false,
}: {
  bids: EvalBidDto[];
  evaluationCompleted?: boolean;
}) => {
  const { t } = useTranslation();
  const count = bids.filter(roundTenderAwaitsAssessment).length;
  if (count === 0) return null;
  return (
    <Alert status="info" rounded="lg">
      <AlertIcon />
      <AlertDescription fontSize="sm">
        {t(
          "{{count}} suppliers delivered a new tender in this round after you assessed their earlier one. Scores and price overrides from before the round do not apply to the round tender",
          { count },
        )}
        {" "}
        {evaluationCompleted
          ? t("Reopen the evaluation to assess the round tender", { count })
          : t("Assess the round tender here", { count })}
      </AlertDescription>
    </Alert>
  );
};

export const EarlierPriceOverrideNote = ({ bid }: { bid: EvalBidDto }) => {
  const { t } = useTranslation();
  const earlier = bid.earlierAssessment?.priceOverride;
  if (earlier?.value == null || bid.priceOverride != null) return null;
  return (
    <Text fontSize="2xs" color="smMuted">
      {t("Set to {{price}} before the round", {
        price: `${formatMoney(earlier.value)} ${earlier.currency}`,
      })}
    </Text>
  );
};

export const CompletedEvaluationStaleBanner = ({ event }: { event: SourcingEventDto }) => {
  const finalStageRound = useFinalStageRound();
  const { data } = useGetEvaluationQuery({ eventId: event.id, round: finalStageRound?.id });
  if (!data) return null;
  return <StaleDecisionsBanner bids={data.bids} evaluationCompleted />;
};
